import { computed, ref, readonly } from 'vue'
import type { ComputedRef, DeepReadonly, Ref } from 'vue'
import { useViewManager } from '../view-manager/useViewManager'
import { WidgetViews } from '@/types'
import type { Message, WorkflowStatus } from '@/composables/websocket/types'
import getAppName from './widget-apps'
import useWebsocket from '@/composables/websocket/useWebsocket'
import { viewsKeys, widgetViews } from './views'
import type { View } from '../view-manager/types'
import type { Track } from '../tracker/useTracker'
import { useEventBus } from '@vueuse/core'
import { useI18n } from '../i18n/useI18n'
import useWidgetOptions from '../widget-options/useWidgetOptions'
import * as Sentry from '@sentry/vue'
import useWidgetHooks from '../widget-hooks/useWidgetHooks'
import useEmbed from '@/embed/code'
import type { WorkflowExecutionPhase } from '@/embed/types'

const {
    changeView,
    currentView,
    currentViewOptions,
} = useViewManager(viewsKeys)

const lastInnerViewOfPreviousView = ref<View>(null)

const { waitForNewMessage, messages, reset: resetWebsocket } = useWebsocket()
const { options: widgetOptions } = useWidgetOptions()

const activeMessage = ref<Message | null>(null)

const workflowPhaseDict: Record<WorkflowStatus, WorkflowExecutionPhase> = {
    CREATED: 'init',
    RUNNING: 'running',
    COMPLETED: 'completed',
    AWAITING: 'init',
    FAILED: 'failed',
    TIMEOUT: 'failed',
    TERMINATED: 'failed',
    PAUSED: 'failed',
    CANCELLED: 'failed',
}

export const getWorkflowExecutionPhase = (workflowStatus?: WorkflowStatus): WorkflowExecutionPhase | undefined => {
    return workflowStatus ? workflowPhaseDict[workflowStatus] : undefined
}

function useWidgetViewManager<M extends Message = Message>() {
    const watchWebSocketMessages = () => {
        useEventBus<Message>('websocket-message').on(message => {
            if (message.workflowStatus === 'TIMEOUT') {
                showTimeoutError()
            }

            if (message.workflowStatus === 'CANCELLED') {
                showCancelledWorkflowError()
            }
        })
    }

    const initialize = () => {
        changeView(WidgetViews.LOADING, { hideBack: true })
    }

    const startWidgetManager = (msg: Message) => {
        const { t } = useI18n()

        setViewMessage(msg)

        if (msg.workflowStatus === 'COMPLETED') {
            return changeView('workflow-finish')
        }

        if (msg.workflowStatus === 'FAILED') {
            return showError({
                title: t('views.widget.initialization_failed.title'),
                description: t('views.widget.initialization_failed.description'),
                retry: true,
                svgIcon: 'bug.svg',
            })
        }

        watchWebSocketMessages()

        // get app view based on
        const appName = getAppName(msg.taskName || msg.taskType, msg.providerName)

        if (appName) {
            return changeView(appName)
        }

        return changeView(WidgetViews.ERROR, {
            errorDetails: 'No app view found for the message',
        })
    }

    const setViewMessage = (value: Message) => {
        activeMessage.value = value

        const {
            partyId,
            taskId,
            taskDefinitionId,
            taskName,
            taskStatus,
            taskType,
            when,
            workflowId,
            workspaceId,
        } = activeMessage.value

        Sentry.setTags({
            task_id: taskId,
            task_definition_id: taskDefinitionId,
            task_name: taskName,
            task_type: taskType,
            workflow_id: workflowId,
            workspace_id: workspaceId,
        })

        useEmbed().emitEventToApi('view-context-changed', {
            partyId,
            taskId,
            taskDefinitionId,
            taskName,
            taskStatus,
            taskType,
            when,
            workflowId,
            workspaceId,
        })
    }

    const setLastInnerView = (value: View) => {
        lastInnerViewOfPreviousView.value = value
    }

    const getAppViewMessages = computed<Message[]>(() => {
        return messages.value.filter((message) => {
            return message.taskId === activeMessage.value?.taskId
        }) as Message[]
    })

    const finishAppView = async (options: {
        track?: Track
        loader?: boolean
    } = {
        loader: true,
    }) => {
        if (options.track) {
            options.track('screenView', 'verificationSuccess')
        }

        if (widgetOptions.value.loading && options.loader) {
            await changeView(WidgetViews.PROCESSING, { hideBack: true })
        }

        const messages = getAppViewMessages.value
        const lastMessage = messages[messages.length - 1]
        const newMessage = await waitForNewMessage(lastMessage.taskId)

        const workflowExecutionPhase = getWorkflowExecutionPhase(newMessage.workflowStatus)
        if (workflowExecutionPhase) {
            useEmbed().setWorkflowExecutionPhase(workflowExecutionPhase)
        }

        const isWorkflowFinish = ['COMPLETED', 'AWAITING', 'FAILED'].includes(newMessage.workflowStatus)

        setViewMessage(newMessage)

        const appName = getAppName(newMessage.taskName || newMessage.taskType, newMessage.providerName)
        const appViewName = isWorkflowFinish ? 'workflow-finish' : appName

        await changeView(appViewName)

        Sentry.logger.trace(`View manager changes app to ${appViewName}`)

        return true
    }

    const currentViewComponent = computed(() => widgetViews[currentView.value as keyof typeof widgetViews])

    const reset = () => {
        activeMessage.value = null
        changeView(WidgetViews.LOADING)
        resetWebsocket()
    }

    const goToPreviousView = () => {
        changeView(currentViewOptions.value.previousView)
    }

    type ShowErrorOptions = {
        title?: string
        description?: string
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        error?: any
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        errorDetails?: any
        retry?: boolean
        close?: boolean
        svgIcon?: string
    }

    const showError = (options: ShowErrorOptions) => {
        const { widgetOpened, setWidgetOpened } = useWidgetHooks()

        if (!widgetOpened.value) {
            setWidgetOpened(true)
        }

        const error = options.error?.response?.data || options.error

        useEmbed().setWorkflowExecutionPhase('failed')

        const errorMessage = Array.isArray(error?.errors)
            ? error?.errors.map((err: Record<string, string>) => err.message || err.code || err.outputMessage).filter(Boolean).join(', ')
            : error?.message || error?.code || error?.outputMessage

        return changeView(WidgetViews.ERROR, {
            ...options,
            error: errorMessage,
            errorDetails: error || options.title,
        })
    }

    const showTimeoutError = (options?: Partial<ShowErrorOptions>) => {
        const { t } = useI18n()
        return showError({
            title: t('views.widget.workflow_timeout.title'),
            description: t('views.widget.workflow_timeout.description'),
            svgIcon: 'timeout.svg',
            retry: false,
            ...options || {},
        })
    }

    const showCancelledWorkflowError = (options?: Partial<ShowErrorOptions>) => {
        const { t } = useI18n()
        showError({
            title: t('views.widget.workflow_cancelled.title'),
            description: t('views.widget.workflow_cancelled.description'),
            svgIcon: 'timeout.svg',
            retry: false,
            close: true,
            ...options || {},
        })
    }

    return {
        initialize,
        changeView,
        currentView,
        currentViewComponent,
        currentViewOptions,
        lastInnerViewOfPreviousView: readonly(lastInnerViewOfPreviousView),
        setLastInnerView,
        startWidgetManager,
        finishAppView,
        getAppViewMessages: getAppViewMessages as ComputedRef<M[]>,
        setViewMessage,
        activeMessage: readonly(activeMessage) as DeepReadonly<Ref<M | null>>,
        reset,
        goToPreviousView,
        showError,
        showTimeoutError,
    }
}

export default useWidgetViewManager
